import {
  Button,
  CloseButton,
  Flex,
  Heading,
  List,
  Modal,
  Text,
  View,
} from "@instructure/ui";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "../../../Card";
import { EMPTY_RTE } from "../../../CustomTextArea/CustomRTE";
import RichTextEditor, {
  rteHasContent,
} from "../../../RichTextArea/RichTextEditor";
import RichTextViewer from "../../../RichTextArea/RichTextViewer";
import { CANVAS, EMBEDDED, EXTERNAL, QUALITATIVE } from "../utils";
import EmbeddedDashboardPreview from "./EmbeddedDashboardPreview";
import ExternalPreview from "./ExternalPreview";
import FlexibleAssessmentPreview from "./FlexibleAssessmentPreview";
import QualitativePreview from "./QualitativePreview";
import TargetedCanvasPreview from "./TargetedCanvasPreview";

const H2_THEME_OVERRIDE = { h2FontWeight: 700 };

const ResultsPreview = ({ open, setOpen, sections, data }) => {
  const { t } = useTranslation();
  const bottomRef = useRef();
  const topRef = useRef();

  return (
    <Modal label={t("results.preview")} size="fullscreen" open={open}>
      <Modal.Header>
        <Heading level="h2" as="h1">
          {t("results.planResultsPreview")}
        </Heading>
        <CloseButton
          placement="end"
          offset="medium"
          onClick={() => setOpen(false)}
          screenReaderLabel={t("global.close")}
        />
      </Modal.Header>
      <Modal.Body>
        <span ref={topRef} />
        <Button
          size="small"
          onClick={() => bottomRef.current.scrollIntoView()}
          margin="0 0 medium 0"
        >
          {t("results.planFindingsAndRecommendations")}
        </Button>
        {rteHasContent(data.headerContent) && (
          <RichTextViewer
            ariaLabel={t("assessment.header")}
            value={data.headerContent}
          />
        )}
        <Flex
          gap="large"
          wrap="wrap"
          margin={rteHasContent(data.headerContent) ? "medium 0 0 0" : "0"}
        >
          {(data.includeMission ||
            (data.mission && data.mission !== EMPTY_RTE)) && (
            <Flex.Item size="100%">
              <Heading
                level="h2"
                border="bottom"
                themeOverride={H2_THEME_OVERRIDE}
              >
                {t("assessment.mission")}:
              </Heading>
              {data.includeMission && (
                <View display="block" padding="small 0 0 large">
                  <Text fontStyle="italic">
                    {t("results.missionAppearsHere")}
                  </Text>
                </View>
              )}
              {data.mission && (
                <View display="block" margin="small 0 0 large">
                  <RichTextViewer
                    value={data.mission}
                    ariaLabel={t("assessment.mission")}
                  />
                </View>
              )}
            </Flex.Item>
          )}
          {(data.includeGoalsObjs || data.goalsObjs) && (
            <Flex.Item size="100%">
              <Heading
                level="h2"
                border="bottom"
                themeOverride={H2_THEME_OVERRIDE}
              >
                {t("dashboard.goalsAndObjectives")}:
              </Heading>
              {data.includeGoalsObjs && (
                <View display="block" padding="small 0 0 large">
                  <Text fontStyle="italic">
                    {t("results.goalsObjectivesAppearsHere")}
                  </Text>
                </View>
              )}
              {data.goalsObjs && (
                <List isUnstyled margin="small 0 0 large">
                  {data.goalsObjs.goals.map((goal) => (
                    <List.Item key={goal.id} margin="0">
                      <Text weight="bold">{goal.description}</Text>
                      <List>
                        {goal.objectives.map((objective) => (
                          <List.Item key={objective.id}>
                            {objective.description}
                          </List.Item>
                        ))}
                      </List>
                    </List.Item>
                  ))}
                </List>
              )}
            </Flex.Item>
          )}
          {sections.map((section, key1) => (
            <Flex.Item key={key1} size="100%">
              <Flex wrap="wrap" gap="small">
                {!!section.name && (
                  <Flex.Item shouldGrow>
                    <Flex wrap="wrap">
                      <Flex.Item>
                        <Heading level="h2" themeOverride={H2_THEME_OVERRIDE}>
                          {section.name}
                        </Heading>
                      </Flex.Item>
                      <Flex.Item size="100%">
                        <View as="div" borderWidth="small 0 0 0" />
                      </Flex.Item>
                    </Flex>
                  </Flex.Item>
                )}
                {section.description !== EMPTY_RTE && (
                  <Flex.Item size="100%" padding="0 0 0 large">
                    <RichTextViewer
                      value={section.description}
                      ariaLabel={t("assessment.sections.sectionDesc")}
                    />
                  </Flex.Item>
                )}
                {section.measures.map((measure, key2) => (
                  <Flex.Item key={key2} size="100%" padding="0 0 0 large">
                    {measure.type === CANVAS &&
                      measure.assessment_type !== "flexible" && (
                        <TargetedCanvasPreview
                          measure={measure}
                          key1={key1}
                          key2={key2}
                        />
                      )}
                    {measure.type === CANVAS &&
                      measure.assessment_type === "flexible" && (
                        <FlexibleAssessmentPreview
                          measure={measure}
                          key1={key1}
                          key2={key2}
                        />
                      )}
                    {measure.type === EXTERNAL && (
                      <ExternalPreview
                        measure={measure}
                        key1={key1}
                        key2={key2}
                      />
                    )}
                    {measure.type === QUALITATIVE && (
                      <QualitativePreview
                        measure={measure}
                        key1={key1}
                        key2={key2}
                      />
                    )}
                    {measure.type === EMBEDDED && (
                      <EmbeddedDashboardPreview
                        measure={measure}
                        key1={key1}
                        key2={key2}
                      />
                    )}
                  </Flex.Item>
                ))}
                <Flex.Item size="100%" padding="0 0 0 large">
                  <RichTextEditor
                    id={`preview-section-findings-${key1}`}
                    label={
                      <Heading level="h3">
                        {t("results.sectionFindings")}
                      </Heading>
                    }
                    defaultValue={t("results.findingsPreviewHtml")}
                    uneditable
                  />
                </Flex.Item>
                <Flex.Item size="100%" padding="0 0 0 large">
                  <RichTextEditor
                    id={`preview-section-recommendations-${key1}`}
                    label={
                      <Heading level="h3">
                        {t("results.sectionRecommendations")}
                      </Heading>
                    }
                    defaultValue={t("results.recommendationPreviewHtml")}
                    uneditable
                  />
                </Flex.Item>
              </Flex>
            </Flex.Item>
          ))}
        </Flex>
        <div
          ref={bottomRef}
          style={{
            borderTop: "1px dashed #ccc",
            marginBottom: 10,
            marginTop: 20,
          }}
        />
        <Card style={{ backgroundColor: "#F1F1F1", height: "auto" }}>
          <Flex wrap="wrap" gap="small">
            <Flex.Item size="100%">
              <RichTextEditor
                id="preview-findings"
                label={
                  <Heading level="h3">{t("results.cycleFindings")}</Heading>
                }
                defaultValue={t("results.findingsPreviewHtml")}
                uneditable
              />
            </Flex.Item>
            <Flex.Item size="100%">
              <RichTextEditor
                id="preview-recommendations"
                label={
                  <Heading level="h3">
                    {t("results.cycleRecommendations")}
                  </Heading>
                }
                defaultValue={t("results.recommendationPreviewHtml")}
                uneditable
              />
            </Flex.Item>
          </Flex>
        </Card>
        <Button
          size="small"
          onClick={() => topRef.current.scrollIntoView()}
          margin="medium 0 0 0"
        >
          {t("results.returnToTop")}
        </Button>
      </Modal.Body>
    </Modal>
  );
};

export default ResultsPreview;
